import { createMutationSetElementMetaData } from "@faro-lotv/service-wires";
import { AutodeskService } from "@services/integrations-service/autodesk/autodesk-service";
import { ProcoreService } from "@services/integrations-service/procore/procore-service";
import { isProjectIntegrations } from "@src/services/integrations-service/integrations-type-guards";
/** Service that provides methods to manage 3rd party integrations with a project */
export class IntegrationsService {
  #projectApiClient;
  #services;
  constructor({
    projectApiClient,
    reauthorize
  }) {
    this.#projectApiClient = projectApiClient;
    this.#services = {
      procore: new ProcoreService({
        reauthorize
      }),
      autodesk: new AutodeskService({
        reauthorize
      })
    };
  }

  /**
   * @returns The ProjectIntegrations entity of the current project
   * If the project connection data still has the Legacy (HB) data format update it to the XG format
   * @throws {ProjectApiError} if it fails to fetch data from Project Api
   * @throws {Error} if it fails to update the data format
   */
  async getProjectIntegrations() {
    const root = await this.#projectApiClient.getRootIElement();
    const projectIntegrations = root.metaDataMap?.projectIntegrations;
    if (!projectIntegrations) {
      return;
    }
    if (!isProjectIntegrations(projectIntegrations)) {
      throw Error("Failed to validate the project integrations object");
    }
    return projectIntegrations;
  }

  /**
   * @returns the companies of an integration
   * @throws {Error} if it fails to get the companies
   * @param integrationId ID of the integration
   */
  async getIntegrationCompanies(integrationId) {
    return await this.#services[integrationId].getIntegrationCompanies();
  }

  /**
   * @returns projects for a given company of an integration
   * @throws {Error} if it fails to get the projects
   * @param integrationCompany Integration company entity
   */
  async getIntegrationProjects(integrationCompany) {
    return await this.#services[integrationCompany.integrationId].getIntegrationProjects(integrationCompany.id);
  }

  /**
   * Connects the XG project to an integration project.
   * @returns The updated ProjectIntegrations entity
   */
  async connectProject({
    integrationId,
    id,
    name,
    companyId,
    issuesOrObservationsBcfProjectId,
    rfisBcfProjectId,
    type
  }) {
    const projectIntegrations = (await this.getProjectIntegrations()) ?? {};
    switch (integrationId) {
      case "procore":
        projectIntegrations.procore = {
          projectName: name,
          projectId: id,
          companyId,
          providerId: "procore"
        };
        projectIntegrations["procore.observations"] = {
          bcfProjectId: issuesOrObservationsBcfProjectId
        };
        projectIntegrations["procore.rfis"] = {
          bcfProjectId: rfisBcfProjectId
        };
        break;
      case "autodesk":
        projectIntegrations.autodesk = {
          projectName: name,
          projectId: id,
          hubId: companyId,
          providerId: "autodesk"
        };
        projectIntegrations["autodesk-acc.issues"] = type === "ACC" ? {
          bcfProjectId: issuesOrObservationsBcfProjectId
        } : null;
        projectIntegrations["autodesk-acc.rfis"] = type === "ACC" ? {
          bcfProjectId: rfisBcfProjectId
        } : null;
        projectIntegrations["autodesk-bim360.issues"] = type === "BIM360" ? {
          bcfProjectId: issuesOrObservationsBcfProjectId
        } : null;
        projectIntegrations["autodesk-bim360.rfis"] = type === "BIM360" ? {
          bcfProjectId: rfisBcfProjectId
        } : null;
        break;
    }
    return await this.setProjectIntegrations(projectIntegrations);
  }

  /**
   * Disconnects an integration from a project.
   * To achieve this it sets each project integration value to null for each user level integration
   * @returns The updated ProjectIntegrations entity
   * @param integrationId ID of the integration to disconnect
   * @throws {Error} if the Project Api client is not defined
   * @throws {ProjectApiError} if it fails to update or fetch data from Project Api
   */
  async disconnectProject(integrationId) {
    const projectIntegrations = await this.getProjectIntegrations();
    if (!projectIntegrations) {
      return;
    }
    switch (integrationId) {
      case "procore":
        projectIntegrations.procore = null;
        projectIntegrations["procore.observations"] = null;
        projectIntegrations["procore.rfis"] = null;
        break;
      case "autodesk":
        projectIntegrations.autodesk = null;
        projectIntegrations["autodesk-acc.issues"] = null;
        projectIntegrations["autodesk-acc.rfis"] = null;
        projectIntegrations["autodesk-bim360.issues"] = null;
        projectIntegrations["autodesk-bim360.rfis"] = null;
        break;
    }
    return await this.setProjectIntegrations(projectIntegrations);
  }

  /**
   * Stores the ProjectIntegrations entity in the metadata of the ProjectAPI root IElement
   * @param projectIntegrations ProjectIntegrations entity
   * @returns the updated ProjectIntegrations entity
   * @throws {Error} if the Project Api client is not defined
   * @throws {ProjectApiError} if it fails to update or fetch data from Project Api
   */
  async setProjectIntegrations(projectIntegrations) {
    const {
      id
    } = await this.#projectApiClient.getRootIElement();
    const mutation = createMutationSetElementMetaData(id, [{
      key: "ProjectIntegrations",
      value: projectIntegrations,
      // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by Project Api backend
      skipIfPresent: false
    }]);
    await this.#projectApiClient.applyMutations([mutation]);
    const updatedProjectIntegrations = await this.getProjectIntegrations();
    if (!updatedProjectIntegrations) {
      throw Error("Integrations were updated for the project but they were later not available in the backend.");
    }
    return updatedProjectIntegrations;
  }

  /**
   * Updates the legacy HB integration data to the Sphere XG format.
   * @returns The project integrations map with the updated data format
   * @param integrationId ID of the integration
   * @param data Map with the legacy HB integration data format
   * @throws {Error} if it fails to update the data format.
   */
  async updateDataFormat(integrationId, data) {
    const updatedData = await this.#services[integrationId].updateDataFormat(data);
    return await this.setProjectIntegrations(updatedData);
  }

  /**
   * @returns Whether there is a project integration connected in the legacy data format.
   * @param integrationId ID of the integration
   * @param projectIntegrationsMap Project integrations map
   */
  isConnectedInLegacyFormat(integrationId, projectIntegrationsMap) {
    return this.#services[integrationId].isConnectedInLegacyFormat(projectIntegrationsMap);
  }

  /**
   * @returns Whether there is a project integration connected in the XG data format.
   * @param integrationId ID of the integration
   * @param projectIntegrationsMap Project integrations map
   */
  isConnectedInXGFormat(integrationId, projectIntegrationsMap) {
    return this.#services[integrationId].isConnectedInXGFormat(projectIntegrationsMap);
  }

  /**
   * @returns Whether there is a project integration connected in either the legacy or XG data format.
   * @param integrationId ID of the integration
   * @param projectIntegrationsMap Project integrations map
   */
  isConnected(integrationId, projectIntegrationsMap) {
    return this.#services[integrationId].isConnected(projectIntegrationsMap);
  }
}